import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  fetchReferrers,
  clearReferrers,
  fetchReferrersCsv,
  clearFetchReferrersCsv,
} from 'redux/analytics/actions';

import { Loader } from 'semantic-ui-react';

import AnalyticsSection from 'components/dashboard/analytics-content/AnalyticsSection';
import AnalyticsPlaceholder from 'components/dashboard/analytics-content/AnalyticsPlaceholder';
import AnalyticsLockedContent from 'components/dashboard/analytics-content/AnalyticsLockedContent';
import Pagination from 'components/common/Pagination';
import DownloadButton from 'components/elements/buttons/DownloadButton';

import { referrers as analyticsReferrersFakeData }
  from 'components/dashboard/analytics-content/AnalyticsLockedContent/fakeData.js';

import { performDownload } from 'utils/common';

import './AnalyticsByReferrers.css';

const AnalyticsByReferrers = ({
  date,
  me,
  analyticsReferrers,
  analyticsReferrersError,
  fetchReferrers,
  clearReferrers,
  fetchReferrersCsv,
  clearFetchReferrersCsv,
  referrersCsv,
}) => {
  const { t } = useTranslation();
  const sectionTitle = t("Views by Referrer"); // define section title
  const itemsPerPage = 7; // define how many items must be on page
  const [itemsToDisplay, setItemsToDisplay] = useState({
    start: 1,
    end: itemsPerPage,
  });

  useEffect(() => {
    fetchReferrers(date);
    return () => {
      clearReferrers();
      setItemsToDisplay({ start: 1, end: itemsPerPage });
    };
  }, [date]);

  useEffect(() => {
    if (referrersCsv) {
      const date = new Date();
      performDownload(
        `Referrers_List_for_${me.username}_${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}.csv`,
        referrersCsv,
      );
      setTimeout(() => {
        clearFetchReferrersCsv();
      }, 3000);
    }
  }, [referrersCsv]);

  if (analyticsReferrersError) {
    return null;
  }

  if (analyticsReferrers && Object.keys(analyticsReferrers).length === 0 && me.subscription.ff_advanced_analytics) {
    return <AnalyticsPlaceholder title={sectionTitle} text={t("Looks like you have no data to display yet…")} />;
  }

  const handlePaginationPageClick = (date) => {
    setItemsToDisplay({
      start: date.selected * itemsPerPage + 1,
      end: date.selected * itemsPerPage + itemsPerPage,
    });
  };

  const renderTableData = (analyticsReferrers) => {
    if (!analyticsReferrers) {
      return null;
    }
    const countPages = Math.ceil(Object.keys(analyticsReferrers).length / itemsPerPage);
    return (
      <>
        <table>
          <thead>
            <tr>
              <th className="link-number">{t("#")}</th>
              <th>{t("Referrer")}</th>
              <th>{t("Page Views")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(analyticsReferrers).slice(itemsToDisplay.start - 1, itemsToDisplay.end).map((i, index) => (
              <tr
                key={`analytics_by_referrers_table_row_key_${index}`}
              >
                <td>{index + 1}.</td>
                <td className="link-name" title={i}>
                  <span>{i}</span>
                </td>
                <td className="views">
                  {analyticsReferrers[i].views &&
                    <span className="value">{analyticsReferrers[i].views}&nbsp;&nbsp;</span>
                  }
                  {analyticsReferrers[i].percent &&
                    <span className="value-percent">({analyticsReferrers[i].percent}&#37;)</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {Object.keys(analyticsReferrers).length > itemsPerPage &&
          <Pagination
            containerClassName={'pagination-wrapper'}
            activeClassName={'active'}
            pageCount={countPages}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePaginationPageClick}
          />
        }
      </>
    );
  };

  return (
    <AnalyticsSection
      className="analytics-by-referrers__component"
      header={
        <div className="title">
          <h3>{sectionTitle}</h3>
        </div>
      }
    >
      <div className="analytics-by-referrers-content">
        {!me.subscription.ff_advanced_analytics ?
          <AnalyticsLockedContent>
            {renderTableData(analyticsReferrersFakeData)}
          </AnalyticsLockedContent>
          :
          <>
            {renderTableData(analyticsReferrers)}
            {analyticsReferrers && Object.keys(analyticsReferrers).length > 0 &&
              <DownloadButton onClick={() => { fetchReferrersCsv(date); }}>
                {t("Export to CSV")}
              </DownloadButton>
            }
          </>
        }
        <Loader active={!analyticsReferrers} />
      </div>
    </AnalyticsSection>
  );
};

AnalyticsByReferrers.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  me: PropTypes.object.isRequired,
  analyticsReferrers: PropTypes.object,
  analyticsReferrersError: PropTypes.object,
  fetchReferrers: PropTypes.func.isRequired,
  clearReferrers: PropTypes.func.isRequired,
  fetchReferrersCsv: PropTypes.func.isRequired,
  clearFetchReferrersCsv: PropTypes.func.isRequired,
  referrersCsv: PropTypes.object,
};

AnalyticsByReferrers.defaultProps = {
  analyticsReferrers: null,
  analyticsReferrersError: null,
  referrersCsv: null,
};

const mapStateToProps = state => ({
  analyticsReferrers: state.analyticsReducer.analyticsReferrers,
  analyticsReferrersError: state.analyticsReducer.analyticsReferrersError,
  referrersCsv: state.analyticsReducer.referrersCsv,
  me: state.usersReducer.me,
});

export default connect(
  mapStateToProps,
  {
    fetchReferrers,
    clearReferrers,
    fetchReferrersCsv,
    clearFetchReferrersCsv,
  },
)(AnalyticsByReferrers);
